import {
  NAME_MAX,
  type ChecklistDto,
  type ChecklistGroupDto,
} from '@iac/contracts';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { DeleteButton } from '../../components/DeleteButton';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import {
  IconChevronDown,
  IconChevronUp,
  IconPencilSquare,
} from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import {
  useChecklistGroups,
  useChecklists,
  useCreateChecklistGroup,
  useDeleteChecklistGroup,
  useRenameChecklistGroup,
  useReorderChecklistGroups,
  useReorderGroupLists,
} from './api';
import { checklistsDict } from './i18n';
import { groupedLists } from './lists';

/** `ids` with the one at `index` swapped with its neighbour `by` away. */
function moved(ids: readonly string[], index: number, by: -1 | 1): string[] {
  const next = [...ids];
  [next[index], next[index + by]] = [next[index + by], next[index]];
  return next;
}

/**
 * The groups in their order, each with its lists in theirs. Every move is
 * saved at once; moving a list to another group happens in its form.
 */
export function ChecklistGroups() {
  const t = useDict(checklistsDict);
  usePageTitle(`${t.groups} — ${t.titleSuffix}`);
  const { data: groups } = useChecklistGroups();
  const { data: lists } = useChecklists();
  const header = <PageHeader title={t.groups} />;
  if (!groups || !lists) return header;
  const ids = groups.map((g) => g.id);

  return (
    <>
      {header}
      <p className="form-hint">{t.groupsHint}</p>
      {groups.length === 0 && <EmptyState>{t.noGroups}</EmptyState>}
      {groupedLists(groups, lists).map(({ group, lists: inGroup }, index) => (
        <GroupCard
          key={group.id}
          group={group}
          lists={inGroup}
          index={index}
          ids={ids}
        />
      ))}
      <AddGroupForm />
    </>
  );
}

function GroupCard({
  group,
  lists,
  index,
  ids,
}: {
  group: ChecklistGroupDto;
  lists: ChecklistDto[];
  index: number;
  /** Every group id in the current order — what a move sends. */
  ids: string[];
}) {
  const t = useDict(checklistsDict);
  const [editing, setEditing] = useState(false);
  const reorder = useReorderChecklistGroups();
  const reorderLists = useReorderGroupLists();
  const remove = useDeleteChecklistGroup();
  const listIds = lists.map((l) => l.id);

  return (
    <Panel>
      {editing ? (
        <RenameForm group={group} onDone={() => setEditing(false)} />
      ) : (
        <div className="checklist-groups-head">
          <h2 className="checklist-groups-name">{group.name}</h2>
          <div className="checklist-groups-actions">
            <MoveButtons
              index={index}
              count={ids.length}
              labels={{
                up: t.moveGroupUp(group.name),
                down: t.moveGroupDown(group.name),
              }}
              onMove={(by) => {
                if (!reorder.isPending) reorder.mutate(moved(ids, index, by));
              }}
            />
            <button
              type="button"
              className="btn-icon-ghost"
              aria-label={t.renameGroup(group.name)}
              onClick={() => setEditing(true)}
            >
              <IconPencilSquare />
            </button>
            <DeleteButton
              label={t.deleteGroup(group.name)}
              confirm={t.confirmDeleteGroup(group.name)}
              onConfirm={() => remove.mutate(group.id)}
              disabled={lists.length > 0}
            />
          </div>
        </div>
      )}
      {lists.length === 0 ? (
        <p className="text-hint checklist-groups-empty">{t.groupEmpty}</p>
      ) : (
        <ol className="checklist-groups-lists">
          {lists.map((list, i) => (
            <li key={list.id} className="checklist-groups-list-row">
              <span className="checklist-groups-list-name">{list.name}</span>
              <div className="checklist-groups-actions">
                <MoveButtons
                  index={i}
                  count={lists.length}
                  labels={{
                    up: t.moveListUp(list.name),
                    down: t.moveListDown(list.name),
                  }}
                  onMove={(by) => {
                    if (!reorderLists.isPending)
                      reorderLists.mutate({
                        groupId: group.id,
                        ids: moved(listIds, i, by),
                      });
                  }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/**
 * Up and down, disabled at the ends. A row moved to an end loses the button
 * that moved it, so focus goes to the other one rather than to the page.
 */
function MoveButtons({
  index,
  count,
  labels,
  onMove,
}: {
  index: number;
  count: number;
  labels: { up: string; down: string };
  onMove: (by: -1 | 1) => void;
}) {
  const up = useRef<HTMLButtonElement>(null);
  const down = useRef<HTMLButtonElement>(null);
  const lastMove = useRef<-1 | 1 | null>(null);

  useEffect(() => {
    if (lastMove.current === -1 && index === 0) down.current?.focus();
    if (lastMove.current === 1 && index === count - 1) up.current?.focus();
    lastMove.current = null;
  }, [index, count]);

  const move = (by: -1 | 1) => {
    lastMove.current = by;
    onMove(by);
  };

  return (
    <>
      <button
        ref={up}
        type="button"
        className="btn-icon-ghost"
        aria-label={labels.up}
        disabled={index === 0}
        onClick={() => move(-1)}
      >
        <IconChevronUp />
      </button>
      <button
        ref={down}
        type="button"
        className="btn-icon-ghost"
        aria-label={labels.down}
        disabled={index === count - 1}
        onClick={() => move(1)}
      >
        <IconChevronDown />
      </button>
    </>
  );
}

function RenameForm({
  group,
  onDone,
}: {
  group: ChecklistGroupDto;
  onDone: () => void;
}) {
  const formId = useId();
  const t = useDict(checklistsDict);
  const [name, setName] = useState(group.name);
  const rename = useRenameChecklistGroup();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    rename.mutate({ id: group.id, name }, { onSuccess: onDone });
  }

  return (
    <form onSubmit={submit} className="checklist-groups-rename">
      <Field
        id={`${formId}-name`}
        label={t.groupName}
        type="text"
        autoComplete="off"
        required
        maxLength={NAME_MAX}
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onDone}>
          {t.cancel}
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={rename.isPending}
        >
          {t.save}
        </button>
      </div>
    </form>
  );
}

function AddGroupForm() {
  const formId = useId();
  const t = useDict(checklistsDict);
  const create = useCreateChecklistGroup();
  const [name, setName] = useState('');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate({ name }, { onSuccess: () => setName('') });
  }

  return (
    <Panel title={t.newGroup}>
      <form onSubmit={submit} className="checklist-groups-add">
        <Field
          id={`${formId}-new`}
          label={t.groupName}
          type="text"
          autoComplete="off"
          required
          maxLength={NAME_MAX}
          placeholder={t.newGroupPlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending}
        >
          {t.addGroup}
        </button>
      </form>
    </Panel>
  );
}
